import axios from "axios";
import {
    Box,
    Card,
    CardContent,
    CardMedia, Chip, CircularProgress,
    Divider,
    LinearProgress,
    Skeleton,
    Stack, styled,
    Typography
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import PetsIcon from "@mui/icons-material/Pets";
import React, {useEffect, useState} from "react";
import CameraAltIcon from '@mui/icons-material/CameraAlt';

const CardContentNoPadding = styled(CardContent)(({theme}) => ({
    [theme.breakpoints.up('sm')]: {
        '&:last-child': {
            paddingBottom: 0,
        },
    },
}));

export default function Dashboard() {
    const [snapshot, setSnapshot] = useState("/snapshot/snapshot.jpg");
    const [loading, setLoading] = useState(true);
    const [snapshotLoading, setSnapshotLoading] = useState(false);
    const [servoRunning, setServoRunning] = useState(false);
    const [data, setData] = useState({
        "servoDuration": "",
        "snapShotTimeOut": "",
        "servoStatus": null,
        "cameraStatus": null
    });

    const testServo = async () => {
        setServoRunning(true)

        if (data?.servoDuration) {
            window.flash(`Servo motor started running for ${data.servoDuration}ms`, 'info')
        }

        try {
            await axios.get('/test-servo').then((response) => {
                if (response.data?.servoDuration) {
                    window.flash(`Servo motor rotated for ${response.data.servoDuration}ms`, 'success')
                }
            })
        } catch (error) {
            console.log(error)
            if (error && typeof error === 'object' && 'data' in error) {
                const errorMessage = (error as any).data?.message || 'An unknown error occurred';
                window.flash(errorMessage, 'error');
            }

            setServoRunning(false)
        }
        setServoRunning(false)
    }

    const getSettings = async () => {
        setLoading(true);
        try {
            await axios.get('/settings').then((response) => {
                if (response?.data !== null && response?.data !== undefined) {
                    setData(response.data)
                }
            });
        } catch (error) {
            console.error(error);
            if (error && typeof error === 'object' && 'data' in error) {
                const errorMessage = (error as any).data?.message || 'An unknown error occurred';
                window.flash(errorMessage, 'error');
            }

        }
        setLoading(false);
    };


    const handleSnapshot = async () => {
        setSnapshotLoading(true);
        try {
            await axios.get('/take-snapshot').then((response) => {
                if (response?.data !== null && response?.data !== undefined) {
                    const timestamp = new Date().getTime();
                    setSnapshot(`/snapshot/snapshot.jpg?${timestamp}`);
                }
            });
        } catch (error) {
            console.error(error);
            if (error && typeof error === 'object' && 'data' in error) {
                const errorMessage = (error as any).data?.message || 'An unknown error occurred';
                window.flash(errorMessage, 'error');
            }
        }
        setSnapshotLoading(false);
    };


    useEffect(() => {
        getSettings();
    }, []);

    return (
        <>
            {loading && <LinearProgress color="secondary"/>}

            <Box maxWidth={850} sx={{m: "auto"}} pt={0}>
                {data && (
                    <Stack justifyContent="center" spacing={4} m="auto" mb={2} mt={10} pl={2} pr={2}
                           direction={{xs: 'column', sm: 'row'}}>
                        <Card variant="outlined" sx={{width: '100%'}}>
                            <CardContentNoPadding sx={{flexGrow: 1}}>
                                <Typography variant="h5" component="div" mb={1}>
                                    Information
                                </Typography>


                                <Stack direction="row" justifyContent="space-between" width="100%">
                                    <Typography variant="body2">
                                        {loading ?
                                            <Skeleton sx={{width: 120}}/> : "AI Camera"}
                                    </Typography>
                                    <Typography sx={{fontSize: 14}} color="text.secondary">
                                        {loading ?
                                            <Skeleton sx={{width: 80}}/> : data.cameraStatus === 2 ? "On" : "Off"}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2">
                                        {loading ?
                                            <Skeleton sx={{width: 130}}/> : "Servo Motor"}
                                    </Typography>
                                    <Typography sx={{fontSize: 14}} color="text.secondary">
                                        {loading ?
                                            <Skeleton sx={{width: 80}}/> : data.servoStatus === 2 ? "On" : "Off"}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2">
                                        {loading ?
                                            <Skeleton sx={{width: 140}}/> : "Servo Motor Duration"}
                                    </Typography>
                                    <Typography sx={{fontSize: 14}} color="text.secondary">
                                        {loading ?
                                            <Skeleton sx={{width: 100}}/> : `${data.servoDuration}ms`}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2">
                                        {loading ?
                                            <Skeleton sx={{width: 140}}/> : "Camera Snapshot timeout"}
                                    </Typography>
                                    <Typography sx={{fontSize: 14}} color="text.secondary">
                                        {loading ?
                                            <Skeleton sx={{width: 100}}/> : `${data.snapShotTimeOut}ms`}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2">
                                        {loading ?
                                            <Skeleton sx={{width: 100}}/> : "Status"}
                                    </Typography>
                                    <Typography sx={{fontSize: 14}} color="text.secondary">
                                        {loading ?
                                            <Skeleton sx={{width: 100}}/> : data.snapShotTimeOut ?
                                                <Chip label="Online" size="small" color="success"/> :
                                                <Chip label="Offline" size="small" color="error"/>}
                                    </Typography>
                                </Stack>

                                <Divider sx={{mt: 2, mb: 2}}/>


                                <Stack spacing={2} justifyContent="space-between"
                                       direction={{xs: 'column', sm: 'row'}}>
                                    <LoadingButton loading={servoRunning} variant="contained"
                                                   onClick={testServo}><PetsIcon
                                        sx={{mr: 1}}/>Dose Food</LoadingButton>

                                    <LoadingButton loading={snapshotLoading} variant="outlined"
                                                   onClick={handleSnapshot}><CameraAltIcon
                                        sx={{mr: 1}}/>Take Snapshot</LoadingButton>
                                </Stack>
                            </CardContentNoPadding>
                        </Card>

                        <Card variant="outlined" sx={{width: '100%', position: 'relative'}}>

                            {snapshotLoading ?
                                <Box sx={{
                                    height: 250,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: 'center'
                                }}><CircularProgress/></Box> :
                                <CardMedia
                                    sx={{height: 250}}
                                    image={snapshot}
                                    title="Recent snapshot"
                                />
                            }
                            <Chip
                                sx={{position: 'absolute', top: 8, right: 8}}
                                label={
                                    <Stack direction="row" alignItems="center">
                                        <CameraAltIcon sx={{mr: 1, fontSize: 17}}/>
                                        Recent snapshot
                                    </Stack>
                                } color="secondary"/>
                        </Card>


                    </Stack>
                )}
            </Box>
        </>
    )
}
